import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Skeleton } from '@tb/ui';
import { api } from '../api/endpoints';
import { Screen } from '../components/Layout';
import { useT } from '../i18n/LocaleProvider';
import { StatusBadge } from '../components/StatusBadge';
import { formatBytes, formatDate, shortenBagId } from '../lib/format';

export function VersionsScreen() {
  const { siteId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useT();

  const versionsQuery = useQuery({
    queryKey: ['versions', siteId],
    queryFn: () => api.sites.versions(siteId),
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId: string) => api.sites.restore(siteId, versionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      void navigate(`/sites/${siteId}/edit`);
    },
  });

  return (
    <Screen title={t('versions.title')} back={`/sites/${siteId}`}>
      <div className="tb-screen">
        {restoreMutation.isError ? (
          <Alert tone="danger" title={t('versions.restoreFailed')}>
            {(restoreMutation.error as Error).message}
          </Alert>
        ) : null}

        {versionsQuery.isPending ? (
          <Skeleton height={200} />
        ) : versionsQuery.isError ? (
          <Alert tone="danger">{(versionsQuery.error as Error).message}</Alert>
        ) : versionsQuery.data.length === 0 ? (
          <span className="tb-hint">{t('versions.empty')}</span>
        ) : (
          <Card>
            {versionsQuery.data.map((version) => (
              <div className="version-row" key={version.id}>
                <div className="tb-grow tb-stack" style={{ gap: 4, minWidth: 0 }}>
                  <div className="tb-row">
                    <span style={{ fontWeight: 600 }}>
                      {t('versions.number', { n: version.number })}
                    </span>
                    <StatusBadge status={version.status} />
                  </div>
                  <span className="tb-hint">
                    {formatDate(version.publishedAt ?? version.createdAt)}
                    {version.sizeBytes ? ` - ${formatBytes(version.sizeBytes)}` : ''}
                  </span>
                  {version.bagId ? (
                    <span className="tb-mono tb-hint">{shortenBagId(version.bagId)}</span>
                  ) : null}
                  {version.error ? (
                    <span className="tb-hint" style={{ color: 'var(--tb-danger)' }}>
                      {version.error}
                    </span>
                  ) : null}
                </div>

                {version.status === 'SUPERSEDED' || version.status === 'LIVE' ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon="history"
                    disabled={version.status === 'LIVE' || restoreMutation.isPending}
                    onClick={() => restoreMutation.mutate(version.id)}
                  >
                    {version.status === 'LIVE' ? t('versions.current') : t('versions.restore')}
                  </Button>
                ) : null}
              </div>
            ))}
          </Card>
        )}

        <span className="tb-hint">{t('versions.hint')}</span>
      </div>
    </Screen>
  );
}
